import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import client from "../api/client";

const card = { background: "#fff", borderRadius: 12, boxShadow: "0 3px 14px rgba(0,0,0,.08)", padding: 16 };
const sectionHeader = { background: "linear-gradient(90deg, #22052D, #CCB3D1)", color: "#fff", borderRadius: 10, padding: "10px 12px", marginBottom: 12 };
const label = { fontSize: 12, fontWeight: 700, marginBottom: 4 };
const input = { height: 32, fontSize: 12 };

export default function SystemSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [servers, setServers] = useState([]);
  const [form, setForm] = useState({
    serverName: ".\\SQLEXPRESS",
    databaseName: localStorage.getItem("databaseName") || "VRGERPMASTER",
    useWindowsAuth: true,
    userName: "sa",
    password: "",
    scriptPath: "",
    restorePath: "",
  });
  const [message, setMessage] = useState(location.state?.setupMessage || "");
  const [busy, setBusy] = useState(false);

  const loadServers = async () => {
    try {
      const res = await client.get("/setup/server-names", { skipAuthRedirect: true });
      setServers(res.data || []);
    } catch (e) {
      setMessage(e.userMessage || "Unable to read SQL Server names");
    }
  };

  useEffect(() => { loadServers(); }, []);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const call = async (url, successText) => {
    try {
      setBusy(true);
      setMessage("");
      const res = await client.post(url, form, { skipAuthRedirect: true });
      setMessage(res.data?.message || successText);
      if (res.data?.databaseName) localStorage.setItem("databaseName", res.data.databaseName);
      if (url === "/setup/create-database" && (res.data?.success ?? true)) {
        navigate("/company-setup", { replace: true, state: { setupMessage: "Database created successfully. Please create company details." } });
      }
    } catch (e) {
      setMessage(e.response?.data?.message || e.userMessage || "Operation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ fontSize: 13 }}>
      <div style={card}>
        <div className="d-flex justify-content-between align-items-center mb-3" style={sectionHeader}>
          <div>
            <h5 className="fw-bold mb-1">System Settings</h5>
            <div className="small" style={{ opacity: .9 }}>SQL Server setup, database creation and appsettings connection update.</div>
          </div>
          <button className="btn btn-sm btn-light" onClick={loadServers} disabled={busy}>Refresh Servers</button>
        </div>

        <div className="alert alert-warning py-2 small">
          Automatic SQL Server installation works only on the machine where API is running and requires Administrator permission. For hosted/Plesk server, install SQL Server manually and use only Test/Save/Create Database.
        </div>

        <div className="row g-2">
          <div className="col-md-4">
            <label style={label}>SQL Server Name</label>
            <input list="sqlServers" name="serverName" className="form-control" style={input} value={form.serverName} onChange={change} placeholder=".\\SQLEXPRESS" />
            <datalist id="sqlServers">{servers.map((s, i) => <option key={i} value={s.serverName || s.ServerName || s} />)}</datalist>
          </div>
          <div className="col-md-3"><label style={label}>Database Name</label><input name="databaseName" className="form-control" style={input} value={form.databaseName} onChange={change} /></div>
          <div className="col-md-2 d-flex align-items-end"><label className="small fw-bold"><input type="checkbox" name="useWindowsAuth" checked={form.useWindowsAuth} onChange={change} className="me-1" /> Windows Auth</label></div>
          <div className="col-md-3"><label style={label}>SQL User</label><input name="userName" className="form-control" style={input} value={form.userName} onChange={change} disabled={form.useWindowsAuth} /></div>
          <div className="col-md-3"><label style={label}>SQL Password</label><input type="password" name="password" className="form-control" style={input} value={form.password} onChange={change} disabled={form.useWindowsAuth} /></div>
          <div className="col-md-9"><label style={label}>SQL Script Path</label><input name="scriptPath" className="form-control" style={input} value={form.scriptPath} onChange={change} placeholder="Leave blank to use API/Database/vrgerpmaster.sql" /></div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-3">
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => call("/setup/test-connection", "Connection successful")}>Test Connection</button>
          <button className="btn btn-sm btn-success" disabled={busy} onClick={() => call("/setup/save-connection", "Connection saved")}>Save to appsettings</button>
          <button className="btn btn-sm btn-warning" disabled={busy} onClick={() => call("/setup/create-database", "Database created")}>Create Database from .sql</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => call("/setup/download-sql-server-express", "Installer downloaded")}>Download SQL Server Express</button>
          <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => call("/setup/install-sql-server-express", "Installer started")}>Install SQL Server Express</button>
          <button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => call("/setup/backup-database", "Backup completed")}>Backup Database</button>
        </div>

        <div className="row g-2 mt-3">
          <div className="col-md-9"><label style={label}>Restore Backup File Path (.bak)</label><input name="restorePath" className="form-control" style={input} value={form.restorePath || ""} onChange={change} placeholder="Example: C:\\Users\\YourName\\Documents\\Quanta\\20260522_153000\\VRGERPMASTER_20260522_153000.bak" /></div>
          <div className="col-md-3 d-flex align-items-end"><button className="btn btn-sm btn-outline-dark w-100" disabled={busy || !form.restorePath} onClick={async () => { if (!window.confirm("Restore will replace current database. Continue?")) return; setBusy(true); setMessage(""); try { const res = await client.post("/setup/restore-database", { backupFilePath: form.restorePath, databaseName: form.databaseName }, { skipAuthRedirect: true }); setMessage(res.data?.message || "Database restored"); } catch(e) { setMessage(e.userMessage || "Restore failed"); } finally { setBusy(false); } }}>Restore Database</button></div>
        </div>

        {message && <div className="alert alert-info py-2 small mt-3 mb-0">{message}</div>}
      </div>
    </div>
  );
}
